
import {
  defineComponent, ref, computed, watch,
} from 'vue';
import type { PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import Sidebar from 'primevue/sidebar';
import InputSwitch from 'primevue/inputswitch';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import Loader from '@/components/layout/Loader.vue';
import { NestApiType, nestCall } from '@/composables/nestApi';
import { SpmFilterOperator } from '@/components/table-rest-api/filters';
import type { SpmApiFilter } from '@/components/table-rest-api/filters';
import type { Integration } from '@/composables/useIntegrations';
import { pickCategoryName, pickLocalizedText } from '@/utils/localized-text';

interface CategoryData {
  id_category?: string | number;
  category_name: string;
}

export default defineComponent({
  name: 'IntegrationDetailsSidebar',

  components: {
    Sidebar,
    InputSwitch,
    SpmButton,
    Loader,
  },

  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    integration: {
      type: Object as PropType<Integration | null>,
      default: null,
    },
    shopIntegrationStatus: {
      type: String,
      default: '',
    },
  },

  emits: [
    'update:visible',
    'install',
    'uninstall',
    'toggle-status',
    'configure',
    'cancel-installation',
  ],

  setup(props, { emit }) {
    const { t, locale } = useI18n();
    const loading = ref(false);
    const fullDescription = ref('');
    const documentationUrl = ref('');

    const internalVisible = computed({
      get: () => props.visible,
      set: (value: boolean) => {
        emit('update:visible', value);
      },
    });

    const partnerName = computed(() => {
      if (!props.integration?.partners_data || props.integration.partners_data.length === 0) {
        return '';
      }
      return props.integration.partners_data[0].partner_name || '';
    });

    // G14 — nom / description courte localisés (C10) avec repli sur les champs
    // string legacy. Tolérant à l'absence des champs `*_i18n`.
    const localizedName = computed(() => pickLocalizedText(
      (props.integration as any)?.name_i18n,
      locale.value,
    ) || props.integration?.name || '');

    const localizedShortDescription = computed(() => pickLocalizedText(
      (props.integration as any)?.short_description_i18n,
      locale.value,
    ) || props.integration?.short_description || '');

    const currentStatus = computed(() => props.shopIntegrationStatus || '');

    const isComingSoon = computed(() => props.integration?.status === 'soon');

    // Portée par boutique : intégration réservée par ShopiMind à une sélection de boutiques.
    const isRestricted = computed(() => props.integration?.visibility === 'restricted');

    const hasConfig = computed(() => !!(
      (props.integration?.config?.fields && props.integration.config.fields.length > 0)
      || (props.integration?.config?.steps && props.integration.config.steps.length > 0)
    ));

    // T1 — an installed-but-unconfigured integration (config schema present but
    // not completed / connection not verified) must not be activatable: the
    // toggle is locked until `configured`. Deactivating an active one stays free.
    const isConfigured = computed(() => !!props.integration?.configured);
    const activationLocked = computed(() => currentStatus.value === 'inactive'
      && hasConfig.value && !isConfigured.value);

    const statusIcon = computed(() => {
      switch (currentStatus.value) {
        case 'active': return 'fas fa-check-circle';
        case 'inactive': return 'fas fa-pause-circle';
        case 'pending_auth': return 'fas fa-clock';
        default: return 'fas fa-circle';
      }
    });

    const statusLabel = computed(() => {
      switch (currentStatus.value) {
        case 'active': return t('integrations.status.active');
        case 'inactive': return t('integrations.status.inactive');
        case 'pending_auth': return t('integrations.status.pendingAuth');
        default: return '';
      }
    });

    // Parseur `category_name` + sélection localisée centralisés (G12).
    const getCategoryName = (category: CategoryData): string => pickCategoryName(category.category_name, locale.value);

    const fetchFullDetails = async () => {
      if (!props.integration?.slug) return;

      loading.value = true;
      try {
        const filters: SpmApiFilter[] = [
          {
            field: 'slug',
            operator: SpmFilterOperator.EQUALS,
            values: props.integration.slug,
          },
        ];

        const fields = [
          'description',
          'description_i18n',
          'documentation_url',
        ].join(',');

        const response = await nestCall(
          'get',
          NestApiType.CLIENT_API,
          '/integrations',
          {},
          '',
          undefined,
          true,
          { limit: 1, offset: 0, fields, filters },
        );

        if (response?.data && response.data.length > 0) {
          const detail = response.data[0];
          // G14 — préfère la description localisée (C10) si présente, repli legacy.
          fullDescription.value = pickLocalizedText(detail.description_i18n, locale.value)
            || detail.description || '';
          documentationUrl.value = detail.documentation_url || '';
        }
      } catch (error) {
        console.error('Error fetching integration details:', error);
      } finally {
        loading.value = false;
      }
    };

    // Fetch details when sidebar opens
    watch(() => props.visible, (isVisible) => {
      if (isVisible && props.integration) {
        fullDescription.value = '';
        documentationUrl.value = '';
        fetchFullDetails();
      }
    });

    return {
      t,
      internalVisible,
      loading,
      fullDescription,
      documentationUrl,
      partnerName,
      currentStatus,
      isComingSoon,
      isRestricted,
      hasConfig,
      activationLocked,
      statusIcon,
      statusLabel,
      getCategoryName,
      localizedName,
      localizedShortDescription,
    };
  },
});
